import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  BookOpen, Plus, Search, Loader2, Pencil, Trash2, ArrowLeft, Save, Eye, EyeOff,
  Globe, Lock, X, ChevronRight, ChevronDown, Share2,
} from 'lucide-react';
import { orgsApi, type Organization } from '../api/organizations';
import { ticketsApi, type TicketBrief } from '../api/tickets';
import { aiSendApi } from '../api/v2';
import { kbApi, type KBArticle, type KBBrief } from '../api/kb';
import { Markdown } from '../lib/markdown';
import { useAuth } from '../lib/auth';
import { fmtTime } from '../lib/format';
import { DistrosSection } from '../components/DistrosSection';

export default function KnowledgeBasePage() {
  const { user } = useAuth();
  const isStaff = user?.type === 'staff';
  const { id } = useParams<{ id?: string }>();
  const nav = useNavigate();

  const [items, setItems] = useState<KBBrief[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editor, setEditor] = useState<KBArticle | 'new' | null>(null);
  const [openArticle, setOpenArticle] = useState<KBArticle | null>(null);
  const [sharing, setSharing] = useState<KBArticle | null>(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await kbApi.list(q || undefined)); }
    finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    if (!id) { setOpenArticle(null); return; }
    kbApi.get(id).then(setOpenArticle).catch(() => setOpenArticle(null));
  }, [id]);

  const onSaved = (a: KBArticle) => { setEditor(null); load(); setOpenArticle(a); nav(`/kb/${a.id}`); };
  const onDelete = async (a: KBBrief) => {
    if (!window.confirm(`Удалить «${a.title}»?`)) return;
    await kbApi.remove(a.id);
    if (openArticle?.id === a.id) { setOpenArticle(null); nav('/kb'); }
    load();
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="text-blue-400" size={20} />
        <h1 className="text-2xl font-bold text-white">База знаний</h1>
        <span className="text-xs text-slate-500">({items.length})</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
            <input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Поиск по заголовку, тексту, тегам…"
              className="bg-slate-950 border border-slate-700 rounded-full py-1.5 pl-9 pr-3 text-xs text-white focus:border-blue-500 outline-none w-72" />
          </div>
          {isStaff && (
            <button onClick={() => setEditor('new')}
              className="text-xs flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-3 py-1.5 rounded">
              <Plus size={12} /> Новая статья
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4 items-start">
        <KBSidebar
          items={items} loading={loading}
          openId={openArticle?.id || null}
          isStaff={isStaff}
          onPick={(id) => nav(`/kb/${id}`)} />

        <main className="min-w-0">
          {openArticle ? (
            <article className="bg-slate-900 border border-slate-800 rounded-xl p-6 overflow-hidden min-w-0">
              <header className="mb-4 pb-3 border-b border-slate-800">
                <button onClick={() => { setOpenArticle(null); nav('/kb'); }}
                  className="text-slate-400 hover:text-white text-xs flex items-center gap-1 mb-2">
                  <ArrowLeft size={12} /> К списку
                </button>
                <div className="flex items-start gap-2">
                  <h2 className="text-xl font-bold text-white flex-1">{openArticle.title}</h2>
                  {isStaff && (
                    <div className="flex gap-1 items-center">
                      <button onClick={() => setSharing(openArticle)}
                        title="Отправить клиенту (в тикет / чат)"
                        className="text-xs inline-flex items-center gap-1 bg-blue-600 hover:bg-blue-500 text-white px-2.5 py-1 rounded mr-1">
                        <Share2 size={12} /> Отправить клиенту
                      </button>
                      <button onClick={() => setEditor(openArticle)}
                        className="text-slate-400 hover:text-blue-400 p-1" title="Редактировать">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => onDelete(openArticle as any)}
                        className="text-slate-400 hover:text-red-400 p-1" title="Удалить">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-3 flex-wrap">
                  {openArticle.tags && <span>{openArticle.tags}</span>}
                  <span className="flex items-center gap-1">
                    {openArticle.is_public ? <Globe size={10} /> : <Lock size={10} />}
                    {openArticle.is_public ? 'видно клиентам' : 'только staff'}
                  </span>
                  {!openArticle.is_published && <span className="text-yellow-500">черновик</span>}
                  <span className="ml-auto">{openArticle.author_name || '—'} · {fmtTime(openArticle.updated_at)}</span>
                </div>
              </header>
              <Markdown source={openArticle.body_md || '_(статья пуста)_'} />
            </article>
          ) : (
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 text-center">
              <BookOpen className="text-slate-700 mx-auto mb-2" size={36} />
              <p className="text-sm text-slate-500">Выберите статью слева или создайте новую.</p>
            </div>
          )}
        </main>
      </div>

      {editor !== null && (
        <KBEditorModal initial={editor === 'new' ? null : editor}
                       onClose={() => setEditor(null)} onSaved={onSaved} />
      )}
      {sharing && (
        <KBShareModal article={sharing} onClose={() => setSharing(null)} />
      )}
    </div>
  );
}


function KBShareModal({ article, onClose }:
  { article: KBArticle; onClose: () => void }) {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgQ, setOrgQ] = useState('');
  const [orgId, setOrgId] = useState<string>('');
  const [tickets, setTickets] = useState<TicketBrief[]>([]);
  const [ticketId, setTicketId] = useState<string>('');
  const [alsoChannels, setAlsoChannels] = useState(true);
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [resultOk, setResultOk] = useState(false);

  useEffect(() => { orgsApi.list().then(setOrgs).catch(() => {}); }, []);
  useEffect(() => {
    if (!orgId) { setTickets([]); setTicketId(''); return; }
    ticketsApi.list({ organization_id: orgId, limit: 50 }).then(r => {
      const open = r.items.filter(t => !['closed','resolved','cancelled'].includes(t.status));
      setTickets(open);
      setTicketId(open[0]?.id || '');
    }).catch(() => { setTickets([]); setTicketId(''); });
  }, [orgId]);

  const filtered = useMemo(() => {
    if (!orgQ.trim()) return orgs.slice(0, 30);
    const s = orgQ.toLowerCase();
    return orgs.filter(o => o.name.toLowerCase().includes(s) || o.inn.includes(s)).slice(0, 30);
  }, [orgs, orgQ]);

  const send = async () => {
    if (!orgId) { alert('Выберите организацию'); return; }
    const orgName = orgs.find(o => o.id === orgId)?.name || orgId;
    setBusy(true); setResult(null);
    try {
      if (ticketId) {
        // Через kb endpoint — добавит Message в тикет + опционально в чаты
        const r = await kbApi.postToTicket(article.id, ticketId, {
          also_channels: alsoChannels, internal,
        });
        const chans = ['веб-чат тикета SD-' + String(r.ticket_number || '').padStart(4,'0')];
        if (r.sent_to_tg) chans.push('Telegram');
        if (r.sent_to_max) chans.push('MAX');
        setResult(`Отправлено в «${orgName}»: ${chans.join(' + ')}`);
        setResultOk(true);
      } else {
        // Без тикета — только в чаты org через AI send-to-org
        if (!alsoChannels) {
          setResult('Выберите тикет — без него только каналы доступны.');
          setResultOk(false); setBusy(false); return;
        }
        const text = `📚 Статья: ${article.title}\n\n${article.body_md.slice(0, 3000)}\n\nПолностью: https://sd.devkz.ru/kb/${article.id}`;
        const r = await aiSendApi.send(orgId, text);
        const chans: string[] = [];
        if (r.sent_to_tg) chans.push('Telegram');
        if (r.sent_to_max) chans.push('MAX');
        if (chans.length) {
          setResult(`Отправлено в «${orgName}»: ${chans.join(' + ')}`);
          setResultOk(true);
        } else {
          setResult(`Не отправлено: ${r.notes.join('; ')}`);
          setResultOk(false);
        }
      }
    } catch (e: any) {
      setResult(e?.response?.data?.detail || e?.message || 'Ошибка');
      setResultOk(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-md mx-4 shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">Отправить статью клиенту</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </header>
        <div className="p-4 space-y-3">
          <div className="text-xs text-slate-400">
            Статья: <b className="text-slate-200">{article.title}</b>
          </div>

          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Организация</span>
            <input value={orgQ} onChange={e => setOrgQ(e.target.value)}
              placeholder="Поиск по имени или ИНН…"
              className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white" />
            {!orgId && (
              <ul className="max-h-40 overflow-y-auto mt-1 border border-slate-800 rounded">
                {filtered.map(o => (
                  <li key={o.id}>
                    <button onClick={() => { setOrgId(o.id); setOrgQ(o.name); }}
                      className="w-full text-left px-2 py-1 text-xs hover:bg-slate-800">
                      {o.name} <span className="text-[10px] text-slate-500 font-mono">{o.inn}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {orgId && (
              <button onClick={() => { setOrgId(''); setOrgQ(''); }}
                className="mt-1 text-[10px] text-slate-500 hover:text-white">сменить организацию</button>
            )}
          </label>

          {orgId && (
            <label className="block">
              <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Тикет</span>
              <select value={ticketId} onChange={e => setTicketId(e.target.value)}
                className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm text-white">
                <option value="">— без тикета (только в чаты) —</option>
                {tickets.map(t => (
                  <option key={t.id} value={t.id}>
                    SD-{String(t.number).padStart(4,'0')} · {t.subject.slice(0, 50)}
                  </option>
                ))}
              </select>
            </label>
          )}

          {orgId && (
            <div className="flex flex-col gap-1.5">
              <label className="flex items-center gap-2 text-xs text-slate-300">
                <input type="checkbox" checked={alsoChannels}
                  onChange={e => setAlsoChannels(e.target.checked)} />
                Также в чаты клиента (TG + MAX)
              </label>
              {ticketId && (
                <label className="flex items-center gap-2 text-xs text-slate-300">
                  <input type="checkbox" checked={internal}
                    onChange={e => setInternal(e.target.checked)} />
                  Внутренний комментарий (клиент в SD не увидит)
                </label>
              )}
            </div>
          )}

          {result && (
            <div className={`text-xs px-3 py-2 rounded ${
              resultOk ? 'bg-emerald-900/30 border border-emerald-800 text-emerald-200'
                       : 'bg-red-900/30 border border-red-800 text-red-200'
            }`}>{resultOk ? '✓ ' : '✗ '}{result}</div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-800 -mx-4 px-4 -mb-4 pb-4 mt-3">
            <button onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Закрыть</button>
            <button onClick={send} disabled={busy || !orgId}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold inline-flex items-center gap-1.5">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Share2 size={13} />}
              Отправить
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Сайдбар KB: группировка статей по «разделу» (первый тэг из CSV),
 * collapsable группы, sticky + независимый scroll. Контент справа
 * крутится отдельно.
 */
function KBSidebar({ items, loading, openId, onPick, isStaff }:
  { items: KBBrief[]; loading: boolean; openId: string | null;
    onPick: (id: string) => void; isStaff: boolean }) {
  const groups = useMemo(() => {
    const map = new Map<string, KBBrief[]>();
    for (const a of items) {
      const first = (a.tags || '').split(',')[0].trim() || 'Без раздела';
      const key = first.charAt(0).toUpperCase() + first.slice(1);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(a);
    }
    // Сортируем разделы по убыванию размера; «FAQ из HDE» и подобные
    // FAQ-разделы всегда в самый конец (это служебная KB-выжимка, не основной контент).
    return Array.from(map.entries()).sort((a, b) => {
      const aFaq = /\bFAQ\b/i.test(a[0]);
      const bFaq = /\bFAQ\b/i.test(b[0]);
      if (aFaq !== bFaq) return aFaq ? 1 : -1;
      return b[1].length - a[1].length;
    });
  }, [items]);

  // По умолчанию все группы свёрнуты. Если у пользователя уже есть сохранённое
  // состояние — используем его. Иначе инициализируем collapsed=true для всех.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('kb.collapsed');
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return {};  // заполним после получения groups через useEffect
  });
  // Когда статьи догружены, недостающие группы помечаем как collapsed=true (default).
  const [seedDone, setSeedDone] = useState(false);
  useEffect(() => {
    if (seedDone || items.length === 0) return;
    setCollapsed(prev => {
      const next = { ...prev };
      let touched = false;
      // groups считаются ниже. Соберём ключи вручную, чтобы не зависеть от порядка.
      const keys = new Set<string>();
      for (const a of items) {
        const first = (a.tags || '').split(',')[0].trim() || 'Без раздела';
        keys.add(first.charAt(0).toUpperCase() + first.slice(1));
      }
      for (const k of keys) {
        if (!(k in next)) { next[k] = true; touched = true; }
      }
      if (touched) localStorage.setItem('kb.collapsed', JSON.stringify(next));
      return next;
    });
    setSeedDone(true);
  }, [items, seedDone]);
  const toggleGroup = (k: string) => setCollapsed(c => {
    const next = { ...c, [k]: !c[k] };
    localStorage.setItem('kb.collapsed', JSON.stringify(next));
    return next;
  });

  // Когда меняется openId — один раз раскрываем родительскую группу
  // (чтобы можно было увидеть контекст). Дальше пользователь свободно
  // сворачивает/раскрывает, даже если в группе открыта статья.
  const lastAutoOpenedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!openId || items.length === 0) return;
    if (lastAutoOpenedFor.current === openId) return;
    const art = items.find(a => a.id === openId);
    if (!art) return;
    const first = (art.tags || '').split(',')[0].trim() || 'Без раздела';
    const key = first.charAt(0).toUpperCase() + first.slice(1);
    setCollapsed(prev => {
      if (prev[key] === false) return prev;
      const next = { ...prev, [key]: false };
      localStorage.setItem('kb.collapsed', JSON.stringify(next));
      return next;
    });
    lastAutoOpenedFor.current = openId;
  }, [openId, items]);

  return (
    <aside
      className="bg-slate-900 border border-slate-800 rounded-xl sticky top-24 self-start
                 max-h-[calc(100vh-120px)] overflow-y-auto">
      <DistrosSection isStaff={isStaff} />
      {loading ? (
        <div className="p-4"><Loader2 className="animate-spin text-blue-400 mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="p-4 text-sm text-slate-500 text-center">Ничего не найдено.</div>
      ) : groups.map(([section, arts]) => {
        // show строго по collapsed-флагу. Открытая статья НЕ форсит показ —
        // пользователь должен иметь возможность свернуть группу даже если
        // в ней выбрана статья (статья видна справа по URL и так).
        const show = !collapsed[section];
        return (
          <div key={section} className="border-b border-slate-800 last:border-b-0">
            <button onClick={() => toggleGroup(section)}
              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-slate-800/40 sticky top-0 bg-slate-900 border-b border-slate-800 z-10">
              <span className="flex items-center gap-1.5">
                {show ? <ChevronDown size={12} className="text-slate-500" />
                       : <ChevronRight size={12} className="text-slate-500" />}
                <span className="text-xs font-bold uppercase tracking-wider text-slate-300 truncate">{section}</span>
              </span>
              <span className="text-[10px] text-slate-500 font-mono">{arts.length}</span>
            </button>
            {show && (
              <ul className="divide-y divide-slate-800/60">
                {arts.map(a => (
                  <li key={a.id} onClick={() => onPick(a.id)}
                    className={`px-3 py-2 cursor-pointer hover:bg-slate-800/50 ${
                      openId === a.id ? 'bg-slate-800/70 border-l-2 border-blue-500' : ''}`}>
                    <div className="text-sm text-white truncate flex items-center gap-1.5">
                      {a.is_public
                        ? <Globe size={9} className="text-slate-500 shrink-0" />
                        : <Lock size={9} className="text-yellow-500 shrink-0" />}
                      <span className="truncate">{a.title}</span>
                    </div>
                    {a.snippet && (
                      <div className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{a.snippet}</div>
                    )}
                    <div className="text-[10px] text-slate-600 mt-1 flex items-center gap-1.5">
                      <span>{fmtTime(a.updated_at)}</span>
                      {!a.is_published && <span className="text-yellow-500 ml-auto">черновик</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </aside>
  );
}


function KBEditorModal({ initial, onClose, onSaved }:
  { initial: KBArticle | null; onClose: () => void; onSaved: (a: KBArticle) => void }) {
  const [title, setTitle] = useState(initial?.title || '');
  const [body, setBody] = useState(initial?.body_md || '');
  const [tags, setTags] = useState(initial?.tags || '');
  const [isPublic, setIsPublic] = useState(initial?.is_public || false);
  const [isPublished, setIsPublished] = useState(initial?.is_published ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const payload = { title: title.trim(), body_md: body, tags: tags.trim() || null, is_public: isPublic, is_published: isPublished };
      const r = initial ? await kbApi.update(initial.id, payload) : await kbApi.create(payload);
      onSaved(r);
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || 'Ошибка');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 overflow-y-auto py-10" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-700 rounded-xl w-full max-w-3xl mx-4 shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <header className="p-4 border-b border-slate-800 flex justify-between">
          <h3 className="text-lg font-bold text-white">{initial ? 'Редактировать статью' : 'Новая статья'}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </header>
        <form onSubmit={submit} className="p-4 space-y-3">
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Заголовок</span>
            <input required value={title} onChange={e => setTitle(e.target.value)}
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </label>
          <label className="block">
            <span className="block text-xs text-slate-400 uppercase tracking-wider mb-1">Теги (через запятую)</span>
            <input value={tags} onChange={e => setTags(e.target.value)}
              placeholder="лицензия, активация, troubleshoot"
              className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white focus:border-blue-500 outline-none" />
          </label>
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-slate-400 uppercase tracking-wider">Текст (markdown)</span>
              <button type="button" onClick={() => setPreview(p => !p)}
                className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1">
                {preview ? <><EyeOff size={11} /> Редактирование</> : <><Eye size={11} /> Превью</>}
              </button>
            </div>
            {preview ? (
              <div className="bg-slate-950 border border-slate-700 rounded p-3 min-h-[200px] max-h-[400px] overflow-y-auto">
                <Markdown source={body || '_(пусто)_'} />
              </div>
            ) : (
              <textarea value={body} onChange={e => setBody(e.target.value)}
                rows={14}
                className="w-full bg-slate-950 border border-slate-700 rounded px-3 py-1.5 text-sm text-white font-mono focus:border-blue-500 outline-none" />
            )}
          </div>
          <div className="flex flex-wrap gap-4 pt-2">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} />
              <Globe size={12} /> видно клиентам (публичная)
            </label>
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input type="checkbox" checked={isPublished} onChange={e => setIsPublished(e.target.checked)} />
              опубликована (иначе — черновик)
            </label>
          </div>
          {err && <div className="bg-red-900/30 border border-red-800 text-red-300 px-3 py-2 rounded text-sm">{err}</div>}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-800 -mx-4 px-4 -mb-4 pb-4 mt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded text-slate-300 text-sm">Отмена</button>
            <button type="submit" disabled={busy || !title.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 rounded text-white text-sm font-bold inline-flex items-center gap-1.5">
              <Save size={13} /> {busy ? 'Сохраняю…' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
